import React, { useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Mic, Loader2, Check, Play, Pause, Heart } from 'lucide-react';

/* ── Sent2ment brand palette ──
   Same colour system used by AffirmationCards, kept verbatim so the two
   sections share a cohesive premium feel across the vault. */
const TITLE_COLOR = '#1E2D45';
const BODY_COLOR = '#3F4E66';
const BRAND_GOLD = '#C6A96B';
const SOFT_GOLD = '#D6C3A3';
const CARD_BG = 'linear-gradient(160deg, #FBF6E8 0%, #F4ECD3 60%, #E8DAB1 100%)';
const HALO = 'rgba(251, 246, 232, 0.7)';

/* ── Decorative corner sprig (matches AffirmationCards) ── */
const CornerSprig = ({ position }) => {
  const transforms = {
    'tl': 'translate(8px, 8px)',
    'tr': 'translate(-8px, 8px) scaleX(-1)',
    'bl': 'translate(8px, -8px) scaleY(-1)',
    'br': 'translate(-8px, -8px) scale(-1, -1)',
  };
  const [vAlign, hAlign] = {
    'tl': ['top', 'left'],
    'tr': ['top', 'right'],
    'bl': ['bottom', 'left'],
    'br': ['bottom', 'right'],
  }[position];
  return (
    <svg
      width="34"
      height="34"
      viewBox="0 0 34 34"
      aria-hidden="true"
      style={{
        position: 'absolute',
        [vAlign]: 0,
        [hAlign]: 0,
        opacity: 0.6,
        transform: transforms[position],
        pointerEvents: 'none',
      }}
    >
      <path
        d="M2 32 C 8 26, 12 20, 14 12 M14 12 C 11 14, 8 14, 5 13 M14 12 C 17 14, 20 14, 23 13 M14 12 C 14 9, 16 6, 19 4"
        stroke={BRAND_GOLD}
        strokeWidth="0.9"
        strokeLinecap="round"
        fill="none"
      />
      <circle cx="14" cy="12" r="1.4" fill={BRAND_GOLD} opacity="0.85" />
    </svg>
  );
};

/* ── Calm pill-shaped "Listen to your message" button (mirrors AffirmationCards) ── */
const ListenButton = ({ src, testid, recipientLabel = false }) => {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);

  const togglePlay = () => {
    const a = audioRef.current;
    if (!a) return;
    if (a.paused) {
      const p = a.play();
      if (p && typeof p.catch === 'function') p.catch(() => {});
    } else {
      a.pause();
    }
  };

  const disabled = !src;
  return (
    <div className="w-full flex flex-col items-center">
      <button
        type="button"
        onClick={togglePlay}
        disabled={disabled}
        data-testid={testid}
        aria-label={playing ? 'Pause message' : 'Listen to your message'}
        className="inline-flex items-center justify-center gap-2.5 px-7 py-3.5 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-90"
        style={{
          background: disabled ? 'rgba(255,255,255,0.55)' : 'rgba(198, 169, 107, 0.22)',
          border: `1px solid ${BRAND_GOLD}`,
          borderRadius: '999px',
          color: TITLE_COLOR,
          letterSpacing: '0.08em',
          fontSize: '12px',
          fontWeight: 600,
          fontFamily: 'Georgia, serif',
        }}
      >
        {playing ? <Pause size={13} strokeWidth={1.6} /> : <Play size={13} strokeWidth={1.6} style={{ marginLeft: '1px' }} />}
        {playing ? 'Pause' : 'Listen to your message'}
      </button>
      {recipientLabel && (
        <p
          className="text-[11px] italic mt-3 text-center"
          style={{
            fontFamily: "'Cormorant Garamond', serif",
            color: `${BODY_COLOR}AA`,
            letterSpacing: '0.02em',
          }}
        >
          Listen whenever &amp; wherever you need.
        </p>
      )}
      <audio
        ref={audioRef}
        src={src || undefined}
        preload="metadata"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => setPlaying(false)}
        className="sr-only"
      />
    </div>
  );
};

/* ── Shared pocket-card frame ── */
const PocketFrame = ({ children, testid }) => (
  <motion.article
    initial={{ y: 14, opacity: 0 }}
    animate={{ y: 0, opacity: 1 }}
    transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
    data-testid={testid}
    className="relative mx-auto px-7 py-9 sm:px-10 sm:py-11 overflow-hidden"
    style={{
      background: CARD_BG,
      border: `1.5px solid ${BRAND_GOLD}`,
      borderRadius: '20px',
      boxShadow: `inset 0 0 0 1px rgba(255,255,255,0.45), 0 1px 0 ${HALO}, 0 18px 40px rgba(30, 45, 69, 0.10)`,
      maxWidth: '640px',
    }}
  >
    {/* Decorative corner sprigs */}
    <CornerSprig position="tl" />
    <CornerSprig position="tr" />
    <CornerSprig position="bl" />
    <CornerSprig position="br" />

    {/* Sent2ment Pinyon Script wordmark */}
    <div className="text-center mb-1" aria-hidden="true">
      <span
        style={{
          fontFamily: "'Pinyon Script', cursive",
          color: TITLE_COLOR,
          fontSize: '1.9rem',
          lineHeight: 1,
        }}
      >
        Sent2ment
      </span>
    </div>

    {/* "POCKET KEEPSAKE" embossed label */}
    <div className="text-center mb-3">
      <p
        className="inline-flex items-center gap-2"
        style={{
          color: BRAND_GOLD,
          fontFamily: 'Georgia, serif',
          fontSize: '10px',
          letterSpacing: '0.4em',
          textTransform: 'uppercase',
          fontWeight: 600,
        }}
      >
        <Heart size={11} strokeWidth={1.4} /> Pocket Keepsake
      </p>
    </div>

    {/* Soft gold divider */}
    <div className="flex justify-center mb-7" aria-hidden="true">
      <div
        style={{
          width: '46px',
          height: '1px',
          background: `linear-gradient(to right, transparent, ${BRAND_GOLD}, transparent)`,
        }}
      />
    </div>

    {children}

    {/* Powered by Spoken Echoes™ — soft gold attribution */}
    <p
      className="text-center italic mt-6"
      style={{
        fontFamily: "'Cormorant Garamond', serif",
        color: SOFT_GOLD,
        fontSize: '11px',
        letterSpacing: '0.05em',
        fontWeight: 300,
      }}
      aria-hidden="true"
    >
      Powered by Spoken Echoes™
    </p>
  </motion.article>
);

/* ── Sender (edit) — write message + add audio + preview listen ── */
export const PocketKeepsakeCardEdit = ({
  value,
  onChangeText,
  audioUrl,
  onUpload,
  locked,
  busy,
}) => {
  const inputRef = useRef(null);
  const handlePick = () => { if (!locked && !busy) inputRef.current?.click(); };
  const handleChange = (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (file) onUpload('pocket', file);
  };

  return (
    <PocketFrame testid="vault-edit-pocket-card">
      {/* Written message — primary input */}
      <label
        className="block text-center mb-2"
        style={{
          fontFamily: "'Cormorant Garamond', serif",
          color: TITLE_COLOR,
          fontSize: '1.3rem',
          fontWeight: 500,
          letterSpacing: '0.005em',
        }}
      >
        Your written message
      </label>
      <textarea
        value={value || ''}
        onChange={(e) => onChangeText('pocket_keepsake_message', e.target.value)}
        placeholder="Something gentle for them to carry close…"
        rows={4}
        maxLength={5000}
        disabled={locked}
        data-testid="vault-edit-pocket-textarea"
        className="w-full px-4 py-3 outline-none transition-colors duration-300 resize-none disabled:opacity-60"
        style={{
          background: 'rgba(255, 255, 255, 0.78)',
          color: BODY_COLOR,
          border: `1px solid ${BRAND_GOLD}55`,
          borderRadius: '10px',
          fontFamily: 'Georgia, serif',
          fontSize: '15px',
          lineHeight: '1.7',
          backdropFilter: 'blur(2px)',
          minHeight: '110px',
        }}
      />

      {/* Audio upload — secondary, calm */}
      <div className="mt-6 mb-7 flex flex-col items-center">
        <button
          type="button"
          onClick={handlePick}
          disabled={locked || busy}
          data-testid="vault-edit-pocket-audio-pick"
          className="inline-flex items-center gap-2 transition-all duration-300 disabled:opacity-50"
          style={{
            background: 'transparent',
            color: BRAND_GOLD,
            fontFamily: 'Georgia, serif',
            fontStyle: 'italic',
            fontSize: '12.5px',
            letterSpacing: '0.04em',
          }}
        >
          {busy ? (
            <><Loader2 size={13} className="animate-spin" /> Uploading…</>
          ) : audioUrl ? (
            <><Check size={13} /> Voice added — tap to replace</>
          ) : (
            <><Mic size={13} strokeWidth={1.6} /> Add your voice (optional)</>
          )}
        </button>
        <input
          ref={inputRef}
          type="file"
          accept="audio/*,.mp3,.m4a,.mp4,.aac,.wav,.ogg,.oga,.opus,.webm,.amr,.3gp,.3gpp,.flac"
          onChange={handleChange}
          className="hidden"
          data-testid="vault-edit-pocket-audio-input"
        />
      </div>

      {/* Listen — preview button */}
      <ListenButton
        src={audioUrl}
        testid="vault-edit-pocket-listen"
      />
    </PocketFrame>
  );
};

/* ── Recipient (view) — read written message + listen, no upload.
   Always renders so the recipient sees the full gift structure even
   if the sender left this product quiet. ── */
export const PocketKeepsakeCardView = ({ message, audioUrl }) => {
  return (
    <PocketFrame testid="vault-view-pocket-card">
      {/* The sender's written message — quietly centered, with a calm
          placeholder when the sender chose to leave it blank. */}
      <div
        className="px-5 py-6 mb-6 text-center"
        style={{
          background: 'rgba(255, 255, 255, 0.72)',
          border: `1px solid ${BRAND_GOLD}55`,
          borderRadius: '10px',
          backdropFilter: 'blur(2px)',
          minHeight: '120px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
        data-testid="vault-view-pocket-message-box"
      >
        {message ? (
          <p
            className="italic"
            style={{
              fontFamily: "'Cormorant Garamond', serif",
              color: TITLE_COLOR,
              fontSize: '18px',
              lineHeight: '1.75',
              whiteSpace: 'pre-wrap',
            }}
            data-testid="vault-view-pocket-message"
          >
            “{message}”
          </p>
        ) : (
          <p
            className="italic"
            style={{
              fontFamily: 'Georgia, serif',
              color: `${BODY_COLOR}99`,
              fontSize: '13px',
            }}
          >
            {audioUrl ? 'A voice note for you below.' : 'Kept gently quiet by your sender.'}
          </p>
        )}
      </div>

      {/* Listen — anywhere, anytime */}
      <ListenButton
        src={audioUrl}
        testid="vault-view-pocket-listen"
        recipientLabel
      />
    </PocketFrame>
  );
};
